import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/firebase';
import { loginWithEmail, logoutUser, registerWithEmail, sendPasswordReset } from '../firebase/auth';
import { getUserDocument, upsertUserDocument } from '../firebase/firestore';
import { friendlyFirebaseError } from '../utils/validators';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        try {
          await upsertUserDocument(firebaseUser);
          setProfile(await getUserDocument(firebaseUser.uid));
        } catch (error) {
          console.error('Unable to sync user document', error);
        }
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const register = useCallback(async (values) => {
    try {
      const createdUser = await registerWithEmail(values);
      const profileData = {
        displayName: values.displayName,
        phone: values.phone,
        country: values.phoneCountry,
        countryCode: values.phoneCountryCode,
        phoneCountry: values.phoneCountry,
        phoneCountryCode: values.phoneCountryCode,
        phoneCountryDialCode: values.countryDialCode,
        timezone: values.timezone,
        currency: values.currency,
      };
      await upsertUserDocument(createdUser, profileData);
      setProfile({ uid: createdUser.uid, email: createdUser.email, ...profileData });
      setUser(createdUser);
      return createdUser;
    } catch (error) {
      console.error(error);
      throw new Error(friendlyFirebaseError(error));
    }
  }, []);

  const login = useCallback(async (values) => {
    try {
      return await loginWithEmail(values);
    } catch (error) {
      console.error(error);
      throw new Error(friendlyFirebaseError(error));
    }
  }, []);

  const requestPasswordReset = useCallback(async (email) => {
    try {
      await sendPasswordReset(email);
    } catch (error) {
      console.error(error);
      throw new Error(friendlyFirebaseError(error));
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!user) return null;
    const nextProfile = await getUserDocument(user.uid);
    setProfile(nextProfile);
    return nextProfile;
  }, [user]);

  const logout = useCallback(() => logoutUser(), []);

  const value = useMemo(() => ({ user, profile, loading, authenticated: Boolean(user), register, login, requestPasswordReset, refreshProfile, logout }), [user, profile, loading, register, login, requestPasswordReset, refreshProfile, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuthContext must be used inside AuthProvider');
  return context;
}
